/**
 * Hooks for the live analytics endpoints.
 *
 * Both hooks support time-travel: pass `selectedDate` / `selectedWeekStart` to
 * fetch a specific period; omit (or pass null) to use the API's default
 * (today IST / current Monday IST → falls back to most recent if pipeline
 * hasn't run for that period).
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../config/api';
import type {
  CohortAnalyticsResponse,
  StudentAnalyticsResponse,
} from '../types/analyticsLive';

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useAnalyticsCohort(
  groupId: string | undefined,
  bookId: string | undefined,
  selectedDate: string | null,
) {
  const [state, setState] = useState<AsyncState<CohortAnalyticsResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    if (!groupId || !bookId) return;
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const params = selectedDate ? { date: selectedDate } : undefined;
      const res = await api.get<CohortAnalyticsResponse>(
        `/api/analytics/cohort/${groupId}/${bookId}`,
        { params },
      );
      setState({ data: res.data, loading: false, error: null });
    } catch (e: any) {
      setState({
        data: null,
        loading: false,
        error: e?.response?.data?.detail || e?.message || 'Failed to load cohort analytics',
      });
    }
  }, [groupId, bookId, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { ...state, refetch: fetchData };
}

export function useAnalyticsStudent(
  groupId: string | undefined,
  bookId: string | undefined,
  userId: string | undefined,
  selectedWeekStart: string | null,
) {
  const [state, setState] = useState<AsyncState<StudentAnalyticsResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    if (!groupId || !bookId || !userId) return;
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const params = selectedWeekStart ? { week_start: selectedWeekStart } : undefined;
      const res = await api.get<StudentAnalyticsResponse>(
        `/api/analytics/student/${groupId}/${bookId}/${userId}`,
        { params },
      );
      setState({ data: res.data, loading: false, error: null });
    } catch (e: any) {
      setState({
        data: null,
        loading: false,
        error: e?.response?.data?.detail || e?.message || 'Failed to load student analytics',
      });
    }
  }, [groupId, bookId, userId, selectedWeekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { ...state, refetch: fetchData };
}
